import { darken } from 'polished';
import React, { useMemo } from 'react';
import seedrandom from 'seedrandom';
import styled, { keyframes } from 'styled-components';
import { Arr, shuffle } from '../../../CodesForFoundation';
import { useSvgId } from '../../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../../Legacy';
import { Matrix } from '../../../platform/geom/Matrix';
import { Rect } from '../../../platform/geom/Rect';
import { Colors } from '../../../ui';
import { useResolve } from '../../Container';
import { BoardInfo } from '../../canvas/BoardInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { TextAlignment, TextSettings } from '../../canvas/entities/TextConventions';
import { TextUIField } from '../../canvas/entities/UIField';
import { RenderBounds, RenderBox, RenderInner, RenderSvg, usePose, usePressable } from '../../canvas/visualization/RenderHelpers';
import { RenderText } from '../../canvas/visualization/RenderText';
import { TextRuler } from '../../canvas/visualization/TextRuler';
import { Spinner } from './Spinner';
import { SpinnerItemField } from './SpinnerItemField';
import { SpinnerLayout, SpinnerSegmentDims } from './SpinnerLayout';

const textSettings = TextSettings.from({
    fontFamily: 'Rubik',
    fontColor: 'white',
    fontWeight: 500,
    fontSize: 0,
    lineHeight: 1.25,
    textMargin: 0,
    fontStyle: 'normal',
    maxFontSize: null,
    textAlignH: TextAlignment.middle,
    textAlignV: TextAlignment.middle,
});

const segmentColors = [
    '#FFC168',
    '#FF6C5F',
    '#8E43E7',
    '#00AEFF',
    '#FF4F81',
    '#2DDE98',
    '#B84592',
    '#1CC7D0',
    '#003666',
];

const RenderWinnerAnimation = styled.div`
    animation: ${keyframes`
        0% { opacity: 0; }
        70% { opacity: 0; }
        100% { opacity: 1; }
    `};

    animation-duration: 4s;
    animation-fill-mode: forwards;
    animation-timing-function: ease-out;
`;

const RenderBorder: LFC<{ object: Spinner }> = ({ object }) => {
    const { base, disk } = object.layout;
    return <>
        <RenderBox {...base.toCSS()} background={Colors.white} borderRadius="4px" border={`solid 1px ${Colors.border}`} borderBottom={`solid 3px ${Colors.border}`} />
    </>;
};

const RenderSegmentText = React.memo<{ dims: SpinnerSegmentDims, value: string }>(({ dims, value }) => {
    const { bounds, offset } = useMemo(() => SpinnerItemField.boundsFromOutline(dims), [dims]);
    return <RenderBox {...bounds.toCSS()} transformOrigin="0 0" transform={offset.toCSS()}>
        <RenderText bounds={bounds} settings={textSettings} value={value} customize={x => ({
            left: 0,
            top: 0,
            width: bounds.width,
            height: bounds.height,
        })} />
    </RenderBox>;
});

const RenderFields: LFC<{ object: Spinner }> = ({ object }) => {
    const segments = object.layout.segments.initial;
    const fields = object.uiFields as TextUIField[];
    return <>
        {segments.map((s, i) => (
            <RenderSegmentText key={`f${i}`} dims={s} value={fields[i]?.value} />
        ))}
    </>;
};

const RenderSegments = React.memo<{ layout: SpinnerLayout }>(({ layout }) => {
    const { base, disk } = layout;
    const initial = layout.segments.initial as SpinnerSegmentDims[];
    const scaler = layout.disk.width / 400;

    const colors = initial.map((_, i) => Arr.at(segmentColors, i));
    const m1 = useSvgId();

    return <RenderSvg area={base}>
        <defs>
            <clipPath id={m1.value}>
                <circle cx={base.width / 2} cy={base.height / 2} r={disk.width / 2} />
            </clipPath>
        </defs>
        {initial.map((s, i) => (
            <path key={`f${i}`} d={s.outline.toString()} fill={colors[i]} stroke={colors[i]} />
        ))}
    </RenderSvg>;
});

const RenderWinner = React.memo<{ layout: SpinnerLayout, winnerId: string }>(({ layout, winnerId }) => {
    const { base, disk } = layout;
    const initial = layout.segments.initial as SpinnerSegmentDims[];
    const winner = initial.find(x => x.ref === winnerId);
    if (!winner) return null;

    const color = darken(.2, Arr.at(segmentColors, initial.indexOf(winner)));
    const m1 = useSvgId();

    return <RenderWinnerAnimation>
        <RenderSvg area={base}>
            <defs>
                <clipPath id={m1.value}>
                    <path d={winner.outline.toString()} fill="white" />
                </clipPath>
            </defs>
            <path d={winner.outline.toString()} fill="none" stroke={color} strokeWidth={8} strokeLinejoin="round" />
        </RenderSvg>
    </RenderWinnerAnimation>;
});

const RenderDisk = React.memo<React.PropsWithChildren<{ layout: SpinnerLayout, value: number }>>(({ layout, value, children }) => {
    const { base } = layout;
    const transform = `rotate(${value}rad)`;
    return <RenderBox {...base.toCSS()} {...{ transform, transition: 'transform 3s cubic-bezier(0,.7,.3,1)' }}>
        {children}
    </RenderBox>;
});

const RenderArm = React.memo<{ layout: SpinnerLayout }>(({ layout }) => {
    const { arm } = layout;

    return <RenderBox {...arm.toCSS()}>
        <svg width="100%" height="100%" viewBox="0 0 50 50">
            <path
                d="M4.93406 28.3755C2.35531 26.8753 2.35531 23.1248 4.93407 21.6246L41.1978 0.528001C43.7766 -0.972198 47 0.903052 47 3.90345L47 46.0966C47 49.097 43.7766 50.9722 41.1978 49.472L4.93406 28.3755Z"
                fill={Colors.primaryDark}
            />
        </svg>
    </RenderBox>;
});

const spinTextSettings = TextSettings.from({
    fontSize: 0,
    fontColor: 'white',
    fontWeight: 450,
    fontFamily: 'Rubik',
    fontStyle: 'normal',
    lineHeight: 1.15,
    maxFontSize: 1024,
    textAlignH: TextAlignment.middle,
    textAlignV: TextAlignment.middle,
    textMargin: 2,
});

const spinLabels = [
    'WOOHOO',
    ['BOOM', 'DONK', 'BAMALAM', 'OH YEAH'],
    ['HIT IT', 'BOP IT', 'TWIST IT', 'PULL IT', 'SQUASH IT', 'STONK IT'],
    ['RIGHT\nHERE', 'RIGHT\nNOW', 'RIGHT\nHERE', 'RIGHT\nNOW'],
    ['SPIN ME RIGHT ROUND BABY', 'RIGHT ROUND', 'LIKE A RECORD BABY', 'RIGHT ROUND', 'ROUND ROUND'],
    'HOLY MOLY',
    ['LETS GET DIZZY', 'LETS DANCE'],
    ['PRESS ME', 'TEASE ME', 'SPICE ME', 'JUICE ME', 'HIYOOO', 'https://c.tenor.com/x8v1oNUOmg4AAAAd/tenor.gif'],
    'ROUND\nAND\nROUND',
    'SPIN\nO\nRAMA',
    'SPIN CITY',
    'DIZZY DELIGHT',
    'BLAST OFF',
    'ZIPPY ZAP',
    'CRUSH IT',
    'SPINNY MCSPIN',
    'SPINTER STELLAR',
    'SPINBALL WIZARD'
];

const useSpinLabels = () => {
    const board = useResolve(EditorSession).get(BoardInfo);
    return useMemo(() => ['SPIN', ...shuffle(spinLabels, seedrandom(board.id)).flat()], []);
};

const RenderSpin = React.memo<{ layout: SpinnerLayout, interaction: string, count: number }>(({ layout, interaction, count }) => {
    const { spin } = layout;
    const inner = spin.inflate([spin.width * -.1, spin.width * -.2]);

    const labelOpts = useSpinLabels();
    const label = Arr.at(labelOpts, count);
    const fontSize = TextRuler.fit(label, spinTextSettings, inner.width, inner.height);
    const scaler = layout.disk.width / 400;

    const transform =
        (interaction === 'idle' ? Matrix.translate(0, -2 * scaler).toCSS() : null) ??
        (interaction === 'hover' ? Matrix.translate(0, -4 * scaler).toCSS() : null) ??
        (interaction === 'active' ? Matrix.translate(0, 0).toCSS() : null);

    const foreStyle = {
        ...spin.toCSS(),
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        border: `solid ${4 * scaler}px ${Colors.red800}`,
        borderRadius: '50%',
        background: Colors.red700,
        transform,
        transition: 'transform 75ms ease-out',
    };

    const backStyle = {
        ...spin.toCSS(),
        borderRadius: '50%',
        background: Colors.red800,
    };

    const imgStyle = {
        position: 'absolute' as const,
        left: 0,
        top: 0,
        width: spin.width - (4 * scaler * 2),
        height: spin.height - (4 * scaler * 2),
        borderRadius: '50%',
    };

    return <>
        <RenderBox {...backStyle} />
        <RenderBox {...foreStyle}>
            {label.startsWith('http') ? <>
                <img style={imgStyle} src={label} />
            </> : <>
                <span style={{ ...TextSettings.toCSS(spinTextSettings), fontSize, whiteSpace: 'pre-wrap' }}>{label}</span>
            </>}
        </RenderBox>
    </>;
});

const RenderButton = React.memo<{ layout: SpinnerLayout, name: string, interaction: string, label: string }>(({ layout, name, interaction, label }) => {

    const area = layout[name] as Rect;
    const scaler = layout.disk.width / 400;

    const transform =
        (interaction === 'idle' ? Matrix.translate(0, -2 * scaler).toCSS() : null) ??
        (interaction === 'hover' ? Matrix.translate(0, -4 * scaler).toCSS() : null) ??
        (interaction === 'active' ? Matrix.translate(0, 0).toCSS() : null);

    const backStyle = {
        ...area.toCSS(),
        background: Colors.gray300,
        borderRadius: `${8 * scaler}px`,
    };

    const foreStyle = {
        ...area.toCSS(),
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: `0 ${5 * scaler}px`,
        fontFamily: 'Rubik',
        fontSize: Math.round(area.height / 3.5),
        fontWeight: 500,
        fontColor: Colors.text,
        textAlign: 'center',
        lineHeight: '1.2em',
        background: Colors.gray200,
        borderRadius: `${8 * scaler}px`,
        transform,
        transition: 'transform 75ms ease-out',
    };

    return <>
        <RenderBox {...backStyle} />
        <RenderBox {...foreStyle}>
            {label}
        </RenderBox>
    </>;
});

export const SpinnerVisual: LFC<{ object: Spinner }> = ({ object, children }) => {

    const { beingEdited } = usePose(object);

    const spin = usePressable(object, 'spin');
    const reset = usePressable(object, 'reset');
    const remove = usePressable(object, 'remove');

    // Round used as a key to force animation off when reset
    const roundKey = `${object.round}`;

    // Causes the winner animation to play correctly
    const winnerKey = `${object.value}/${object.winnerId}}`;
    const { winnerVisible } = object;

    return <>
        <RenderBounds object={object}>
            <RenderInner object={object}>
                <RenderBorder object={object} />
                <RenderDisk key={roundKey} layout={object.layout} value={object.value}>
                    <RenderSegments layout={object.layout} />
                    {!!winnerVisible && <RenderWinner key={winnerKey} layout={object.layout} winnerId={object.winnerId} />}
                    {!beingEdited && <RenderFields object={object} />}
                </RenderDisk>
                <RenderArm layout={object.layout} />
                <RenderSpin layout={object.layout} interaction={spin} count={object.count} />
                <RenderButton layout={object.layout} name="reset" interaction={reset} label="Reset Spinner" />
                <RenderButton layout={object.layout} name="remove" interaction={remove} label="Remove Winner" />
                {children}
            </RenderInner>
        </RenderBounds>

    </>;
};
